import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { isFeatureEnabled } from 'src/shared/functions/isFeatureEnabled';
import { SysProductYvNameEnum } from 'src/shared/models/enums/sysProductEnums';
import { SideMenuItem } from 'src/shared/models/gui/sideMenuItem';
import {
  DashboardOutlined as Dashboard,
  PeopleOutlined as People,
  TextsmsOutlined as Textsms,
  AttachEmailOutlined as AttachEmail,
  ReceiptOutlined as Receipt,
  PaidOutlined as Paid,
  VolunteerActivismOutlined as VolunteerActivism,
  ApartmentOutlined as Apartment,
  ManageAccountsOutlined as ManageAccounts,
  SettingsOutlined as Settings,
  EventOutlined as Event,
  ContactSupportOutlined as ContactSupport,
  InsightsOutlined as Insights,
  Inventory2Outlined as Inventory2,
  PaymentOutlined as Payment,
  ReceiptLongOutlined as ReceiptLong,
  ContactPageOutlined as ContactPage,
  AttachFileOutlined as AttachFile,
  TroubleshootOutlined as Troubleshoot,
} from '@mui/icons-material';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { useEInvoiceApplicationsBadge } from 'src/modules/invoice/hooks/useEInvoiceApplicationsBadge';
import { useMemberUpdatesBadge } from 'src/modules/member/hooks/useMemberUpdatesBadge';
import { useUnmatchedPaymentsBadge } from 'src/modules/invoice/hooks/useUnmatchedPaymentsBadge';
import { useAutogiroUnlinkedBadge } from 'src/modules/invoice/hooks/useAutogiroUnlinkedBadge';
import { useUnaccountedPaymentsBadge } from 'src/modules/invoice/hooks/useUnaccountedPaymentsBadge';
import { useHasSwishRecurringAccount } from 'src/modules/invoice/hooks/useHasSwishRecurringAccount';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { getSysProducts } from 'src/shared/functions/getSysProducts';
import { User } from 'src/shared/models/user';

export const useSideMenuUser = () => {
  const { currentUser } = useGlobals();

  const sysProducts = getSysProducts(currentUser);
  const hasInvoice = sysProducts.some((x) => x.yvName.toLowerCase() === SysProductYvNameEnum.Invoice.toLowerCase());

  const [hasPendingEInvoiceApplications] = useEInvoiceApplicationsBadge(hasInvoice);
  const pendingMemberUpdates = useMemberUpdatesBadge();
  const unmatchedPayments = useUnmatchedPaymentsBadge(hasInvoice);
  const [autogiroUnlinkedCount] = useAutogiroUnlinkedBadge(hasInvoice);
  const unaccountedPayments = useUnaccountedPaymentsBadge(hasInvoice);
  const hasSwishRecurringAccount = useHasSwishRecurringAccount(hasInvoice);

  const otherLabel = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.OtherLabelPlural, 'sideMenu.memberOtherContact');
  const useOther = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.UseOther, '') === 'True';

  const getOrganizationLink = (user: User): RoutesEnum => {
    if (!user) return RoutesEnum.OrganizationAssocations;

    if (user.districtCount > 0) {
      return getPermission(user, PermissionEnum.MemberReadAll) ? RoutesEnum.Organization : RoutesEnum.OrganizationDistricts;
    }

    return RoutesEnum.OrganizationAssocations;
  };

  const displayOrganization = (): boolean => {
    if (!!currentUser && getPermission(currentUser, PermissionEnum.MemberReadAll) && currentUser.associationCount > 0) {
      return true;
    }
    return false;
  };

  // TODO: Lägga in permission på menuitems (PermissionEnums) - se updatedMembers
  const sideMenuUserItems: SideMenuItem[] = [
    {
      yvName: SysProductYvNameEnum.MemberReg,
      key: 'dashboard',
      showMenuItem: true,
      label: 'sideMenu.dashboard',
      link: RoutesEnum.Dashboard,
      Icon: <Dashboard />,
    },
    {
      yvName: SysProductYvNameEnum.MemberReg,
      key: 'members',
      showMenuItem: true,
      label: 'sideMenu.members',
      link: RoutesEnum.Members,
      Icon: <People />,
      subMenuItems: [
        // {
        //   yvName: SysProductYvNameEnum.MemberReg,
        //   key: 'importmembers',
        //   showMenuItem: true,
        //   label: 'sideMenu.importMembers',
        //   link: RoutesEnum.ImportMembers,
        //   Icon: <People />,
        // },
        {
          yvName: SysProductYvNameEnum.MemberReg,
          key: 'members',
          showMenuItem: true,
          label: 'sideMenu.memberRegister',
          link: RoutesEnum.Members,
          Icon: <People />,
        },
        {
          yvName: SysProductYvNameEnum.MemberReg,
          key: 'updatedMembers',
          permission: PermissionEnum.MemberApprove,
          showMenuItem: true,
          label: 'sideMenu.memberUpdates',
          link: RoutesEnum.MemberUpdates,
          Icon: <People />,
          badgeCount: pendingMemberUpdates,
        },
        {
          yvName: SysProductYvNameEnum.MemberReg,
          key: 'invoiceAddress',
          permission: PermissionEnum.InvoiceAddressRead,
          showMenuItem: true,
          hideIfNoPermission: true,
          label: 'sideMenu.invoiceAddress',
          link: RoutesEnum.MemberInvoiceAddress,
          Icon: <Receipt />,
        },
        {
          yvName: SysProductYvNameEnum.MemberReg,
          key: 'memberOtherContact',
          showMenuItem: useOther,
          label: otherLabel,
          link: RoutesEnum.MemberOtherContact,
          Icon: <Event />,
        },
      ],
    },
    {
      yvName: SysProductYvNameEnum.MemberReg, // TODO
      key: 'statisticdashboard',
      showMenuItem: true,
      label: 'sideMenu.statistics.statistics',
      link: RoutesEnum.Statistics,
      Icon: <Insights />,
      subMenuItems: [
        {
          yvName: SysProductYvNameEnum.MemberReg,
          key: 'statistics',
          showMenuItem: true,
          label: 'sideMenu.statistics.overview',
          link: RoutesEnum.Statistics,
          Icon: <Event />,
        },
        {
          yvName: SysProductYvNameEnum.MemberReg,
          key: 'statisticsMemberProgress',
          showMenuItem: true,
          label: 'sideMenu.statistics.memberProgress',
          link: RoutesEnum.StatisticsMemberProgress,
          Icon: <Event />,
        },
        {
          yvName: SysProductYvNameEnum.MemberReg,
          key: 'statisticsReports',
          showMenuItem: true,
          label: 'sideMenu.statistics.reports',
          link: RoutesEnum.StatisticsReports,
          Icon: <Event />,
        },
        {
          yvName: SysProductYvNameEnum.MemberReg,
          key: 'statisticsTroubleshooting',
          showMenuItem: true,
          label: 'sideMenu.statistics.troubleshooting',
          link: RoutesEnum.StatisticsTroubleshooting,
          Icon: <Troubleshoot />,
        },
        // {
        //   yvName: SysProductYvNameEnum.MemberReg,
        //   key: 'statisticsIncome',
        //   showMenuItem: true,
        //   label: 'sideMenu.statistics.income',
        //   link: RoutesEnum.StatisticsIncome,
        //   Icon: <Event />,
        // },
      ],
    },
    {
      yvName: SysProductYvNameEnum.MemberReg,
      key: 'groupmessage',
      showMenuItem: true,
      label: 'sideMenu.groupMessage',
      link: RoutesEnum.GroupMessageEmailOverview,
      Icon: <Textsms />,
      subMenuItems: [
        {
          yvName: SysProductYvNameEnum.MemberReg,
          key: 'mailSending',
          showMenuItem: true,
          label: 'sideMenu.mailSending',
          link: RoutesEnum.GroupMessageEmailOverview,
          Icon: <AttachEmail />,
        },
        {
          yvName: SysProductYvNameEnum.MemberReg,
          key: 'smsSending',
          permission: PermissionEnum.GroupMessageSmsRead,
          showMenuItem: true,
          label: 'sideMenu.smsSending', // TODO: Ny modul
          link: RoutesEnum.GroupMessageSmsOverview, // TODO: Ny modul
          Icon: <AttachEmail />, // TODO: Ny modul
        },
      ],
    },
    {
      yvName: SysProductYvNameEnum.Invoice,
      key: 'invoice',
      showMenuItem: true,
      label: 'sideMenu.invoicing',
      link: RoutesEnum.InvoiceDashboard,
      Icon: <Receipt />,
      subMenuItems: [
        {
          yvName: SysProductYvNameEnum.Invoice,
          key: 'invoices',
          showMenuItem: true,
          label: 'sideMenu.invoices',
          link: RoutesEnum.Invoices,
          Icon: <People />,
        },
        {
          yvName: SysProductYvNameEnum.Invoice,
          key: 'invoiceMissions',
          showMenuItem: true,
          label: 'sideMenu.invoiceMissions',
          link: RoutesEnum.InvoiceMission,
          Icon: <People />,
        },
        {
          yvName: SysProductYvNameEnum.Invoice,
          key: 'allPayments',
          showMenuItem: true,
          label: 'sideMenu.allPayments',
          link: RoutesEnum.Payments,
          Icon: <Paid />,
        },
        {
          yvName: SysProductYvNameEnum.Invoice,
          key: 'payments',
          showMenuItem: true,
          label: 'sideMenu.incomingPayments',
          link: RoutesEnum.IncomingBankPayment,
          Icon: <Paid />,
          badgeCount: unmatchedPayments,
        },
        {
          yvName: SysProductYvNameEnum.Invoice,
          key: 'accounts',
          showMenuItem: true,
          label: 'sideMenu.paymentAccounting',
          link: RoutesEnum.PaymentTransactions,
          Icon: <Paid />,
          badgeCount: unaccountedPayments,
        },
        // Döljer Mallar enligt KH 2023-04-14
        // {
        //   yvName: SysProductYvNameEnum.Invoice,
        //   key: 'invoiceTemplate',
        //   showMenuItem: isFeatureEnabled(),
        //   label: 'sideMenu.invoiceTemplate',
        //   link: RoutesEnum.InvoiceTemplate,
        //   Icon: <People />,
        // },
        // Visar Avsieringar på PROD enligt KH 2025-03-07
        {
          yvName: SysProductYvNameEnum.Invoice,
          key: 'autogiro',
          showMenuItem: true,
          label: 'sideMenu.autogiro',
          link: RoutesEnum.Autogiro,
          Icon: <Paid />,
          badgeCount: autogiroUnlinkedCount,
        },
        {
          yvName: SysProductYvNameEnum.Invoice,
          key: 'eInvoice',
          showMenuItem: true,
          label: 'sideMenu.eInvoice',
          link: RoutesEnum.EInvoice,
          Icon: <Receipt />,
          badgeCount: hasPendingEInvoiceApplications,
        },
        {
          yvName: SysProductYvNameEnum.Invoice,
          key: 'memberStripeStatus',
          showMenuItem: isFeatureEnabled(),
          label: 'sideMenu.memberStripeStatus',
          link: RoutesEnum.StripeCustomer,
          Icon: <Payment />,
        },
        {
          yvName: SysProductYvNameEnum.Invoice,
          key: 'memberSwishStatus',
          // Visa Swish Återkommande för alla kunder som har minst en fakturagrupp med ett Swish Återkommande-nummer
          // (swishRecurringAccount) ifyllt. Kontrolleras via InvoiceGroup-anropet i useHasSwishRecurringAccount.
          showMenuItem: hasSwishRecurringAccount,
          label: 'sideMenu.memberSwishStatus',
          // Öppna Swish Återkommande på Medgivanden-fliken (som autogiro öppnar på medgivanden).
          link: RoutesEnum.SwishConsents,
          Icon: <Payment />,
        },
        {
          yvName: SysProductYvNameEnum.Invoice,
          key: 'articleRegister',
          showMenuItem: true,
          label: 'sideMenu.articleRegister',
          link: RoutesEnum.InvoiceArticleRegister,
          Icon: <Inventory2 />,
        },
      ],
    },
    {
      yvName: SysProductYvNameEnum.Fundraiser,
      key: 'gift',
      showMenuItem: true,
      label: 'sideMenu.donations',
      link: RoutesEnum.Fundraiser,
      Icon: <VolunteerActivism />,
      subMenuItems: [
        {
          yvName: SysProductYvNameEnum.Fundraiser,
          key: 'fundraiser',
          showMenuItem: true,
          label: 'sideMenu.fundraiser',
          link: RoutesEnum.Fundraiser,
          Icon: <VolunteerActivism />,
        },
        {
          yvName: SysProductYvNameEnum.Fundraiser,
          key: 'gifts',
          showMenuItem: true,
          label: 'sideMenu.gifts',
          link: RoutesEnum.Gifts,
          Icon: <VolunteerActivism />,
        },
        {
          yvName: SysProductYvNameEnum.Fundraiser,
          key: 'donors',
          showMenuItem: true,
          label: 'sideMenu.donors',
          link: RoutesEnum.Donors,
          Icon: <VolunteerActivism />,
        },
      ],
    },
    {
      yvName: SysProductYvNameEnum.Event,
      key: 'events',
      showMenuItem: true,
      label: 'sideMenu.events',
      link: RoutesEnum.Events,
      Icon: <Event />,
      subMenuItems: [
        {
          yvName: SysProductYvNameEnum.Event,
          key: 'eventsView',
          showMenuItem: true,
          label: 'sideMenu.events',
          link: RoutesEnum.Events,
          Icon: <Event />,
        },
      ],
    },
    // {
    //   yvName: '',
    //   key: 'sending',
    //   showMenuItem: true,
    //   label: 'sideMenu.sending',
    //   link: RoutesEnum.Sending,
    //   Icon: <Print />,
    // },
    // {
    //   label: 'sideMenu.editMember',
    //   link: RoutesEnum.EditMember,
    //   Icon: Person,
    // },
    // {
    //   yvName: '',
    //   key: 'admin',
    //   showMenuItem: true,
    //   label: 'sideMenu.admin.overview',
    //   link: AdminRoutesEnum.AdminOverview,
    //   Icon: <Build />,
    // },
    // {
    //   yvName: 'file',
    //   key: 'file',
    //   showMenuItem: false,
    //   label: 'sideMenu.file',
    //   link: RoutesEnum.FileOverview,
    //   Icon: <FolderOpen />,
    // },
    {
      yvName: SysProductYvNameEnum.Organisation,
      key: 'system-organization',
      showMenuItem: !!currentUser && (currentUser?.districtCount > 0 || currentUser?.associationCount > 0),
      label: 'sideMenu.organization',
      link: currentUser ? getOrganizationLink(currentUser) : RoutesEnum.OrganizationAssocations,
      Icon: <Apartment />,
      subMenuItems: [
        {
          yvName: SysProductYvNameEnum.Organisation,
          key: 'system-organization',
          showMenuItem: displayOrganization(),
          label: 'sideMenu.organizationSideMenu',
          link: RoutesEnum.Organization,
          Icon: <ManageAccounts />,
        },
        {
          yvName: SysProductYvNameEnum.Organisation,
          key: 'system-districts',
          showMenuItem: !!currentUser && currentUser?.districtCount > 0,
          label: 'sideMenu.districts',
          link: RoutesEnum.OrganizationDistricts,
          Icon: <ManageAccounts />,
        },
        {
          yvName: SysProductYvNameEnum.Organisation,
          key: 'system-associations',
          showMenuItem: !!currentUser && currentUser?.associationCount > 0,
          label: 'sideMenu.associations',
          link: RoutesEnum.OrganizationAssocations,
          Icon: <ManageAccounts />,
        },
      ],
    },
    {
      yvName: SysProductYvNameEnum.Settings,
      key: 'system-settings',
      showMenuItem: true,
      label: 'sideMenu.settings',
      link: RoutesEnum.Settings,
      Icon: <Settings />,
      subMenuItems: [
        {
          yvName: SysProductYvNameEnum.Settings,
          key: 'system-users',
          showMenuItem: true,
          label: 'sideMenu.users',
          permission: PermissionEnum.UserCreate,
          link: RoutesEnum.Users,
          Icon: <ManageAccounts />,
          description: 'menuItemDescription.users',
        },
        {
          yvName: SysProductYvNameEnum.Settings,
          key: 'system-invoice',
          showMenuItem: true,
          permission: PermissionEnum.InvoiceCreate,
          label: 'sideMenu.adminInvoiceGroupSettings',
          link: RoutesEnum.AdminInvoiceGroupSettings,
          Icon: <ReceiptLong />,
          description: 'menuItemDescription.adminInvoiceGroupSettings',
        },
        {
          yvName: SysProductYvNameEnum.Settings,
          key: 'system-memberRegistrySettings',
          showMenuItem: true,
          permission: PermissionEnum.MemberProfileCreate,
          label: 'sideMenu.memberRegistrySettings',
          link: RoutesEnum.MemberRegistrySettings,
          Icon: <Settings />,
          description: 'menuItemDescription.memberRegistrySettings',
        },
        {
          yvName: SysProductYvNameEnum.Settings,
          key: 'system-fundraiserSettings',
          showMenuItem: true,
          permission: PermissionEnum.FundraiserRead,
          label: 'sideMenu.fundraiserSettings',
          link: RoutesEnum.FundraiserSettings,
          Icon: <VolunteerActivism />,
          description: 'menuItemDescription.fundraiserSettings',
        },
        {
          yvName: SysProductYvNameEnum.Settings,
          key: 'system-organizationAccountDetails',
          showMenuItem: isFeatureEnabled(),
          permission: PermissionEnum.UserRead,
          label: 'sideMenu.organizationAccountDetails',
          link: RoutesEnum.CustomerSettings,
          Icon: <ContactPage />,
          description: 'menuItemDescription.organizationAccountDetails',
        },
        {
          yvName: SysProductYvNameEnum.Settings,
          key: 'system-autojobSettings',
          showMenuItem: true,
          permission: PermissionEnum.InvoiceCreateForCustomer,
          label: 'sideMenu.autojobSettings',
          link: RoutesEnum.AutojobSettings,
          Icon: <Settings />,
          description: 'menuItemDescription.autojobSettings',
        },
        {
          yvName: SysProductYvNameEnum.Settings,
          key: 'system-eventSettings',
          showMenuItem: isFeatureEnabled(),
          label: 'sideMenu.eventSettings', // TODO: Ny modul
          link: RoutesEnum.MemberFees, // TODO: Ny modul
          Icon: <Event />, // TODO: Ny modul
          description: 'menuItemDescription.eventSettings',
        },
      ],
    },
    {
      yvName: SysProductYvNameEnum.Support,
      key: 'support',
      showMenuItem: true,
      label: 'sideMenu.support',
      link: RoutesEnum.Support,
      Icon: <ContactSupport />,
      subMenuItems: [
        {
          yvName: SysProductYvNameEnum.Support,
          key: 'support-invoiceData',
          showMenuItem: true,
          label: 'sideMenu.invoiceData',
          link: RoutesEnum.Support,
          Icon: <ContactSupport />,
        },
        {
          yvName: SysProductYvNameEnum.Support,
          key: 'support-installCustomer',
          showMenuItem: true,
          label: 'sideMenu.installCustomer',
          link: RoutesEnum.InstallCustomer,
          Icon: <ContactSupport />,
        },
        {
          // Uppdragen är Föreningssupports egna och ligger i fsupport-tenanten – sidan visas bara där.
          yvName: SysProductYvNameEnum.Support,
          key: 'support-bookedSendingMissions',
          showMenuItem: currentUser?.custName?.toLowerCase() === 'fsupport',
          label: 'sideMenu.bookedSendingMissions',
          link: RoutesEnum.BookedSendingMissions,
          Icon: <ContactSupport />,
        },
      ],
    },
    {
      yvName: SysProductYvNameEnum.DocumentArchive,
      key: 'document-archive',
      showMenuItem: isFeatureEnabled(),
      label: 'sideMenu.documentArchive',
      link: RoutesEnum.DocumentArchiveOverview,
      Icon: <AttachFile />,
      subMenuItems: [
        {
          yvName: SysProductYvNameEnum.DocumentArchive,
          key: 'documents',
          showMenuItem: isFeatureEnabled(),
          label: 'sideMenu.documents',
          link: RoutesEnum.DocumentArchiveOverview,
          Icon: <AttachFile />,
        },
      ],
    },
  ];

  return sideMenuUserItems;
};
